import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  DEFAULT_SETTINGS,
  UBICACIONES_FIJAS,
  ZERO,
  costOf,
  esCamion,
  formatMoney,
  formatQuantity,
  hoy,
  newId,
  parseQuantity,
  ubicacionCamion,
  type Centavos,
} from '@abastece/shared';
import type { Producto } from '@abastece/core';
import { existenciaId } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { enRango } from '../lib/costos.js';
import { emitir } from '../lib/emitir.js';
import { anunciar } from '../lib/hecho.js';
import { usePuede } from '../lib/sesion.js';
import { volver } from '../lib/volver.js';
import { BuscadorProducto } from '../ui/BuscadorProducto.js';
import { Campo, Selector } from '../ui/Campo.js';
import { Pagina } from '../ui/Pagina.js';
import { Ico } from '../ui/Iconos.js';
import { useT } from '../i18n/index.js';
import { useNombreProducto } from '../i18n/productos.js';
import { etiquetaLugar, etiquetaUnidad } from '../i18n/dominio.js';

interface Linea {
  key: string;
  producto: Producto;
  cantidad: string;
}

export function Traslado() {
  const t = useT();
  const nombre = useNombreProducto();
  const existencias = useLiveQuery(() => db.existencias.toArray(), [], []);
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  /** What moves is worth what the books say it cost: said to the owner, not to a cashier. */
  const verCostos = usePuede('ver_costos');

  const lugares = UBICACIONES_FIJAS.map((u) => ({ valor: u as string, nombre: etiquetaLugar(u) }));
  /**
   * A truck is a stock location, and was loaded by a transfer like any other until the owner took
   * «Reparto» out (2026-10-04). Nothing goes onto one now, but what an old route left on it is
   * still there, so a truck holding stock is somewhere to take it from, never to put it.
   */
  const camiones = [
    ...new Set(existencias.filter((e) => esCamion(e.ubicacion) && e.cantidad > 0).map((e) => e.ubicacion)),
  ].map((valor) => ({
    valor,
    nombre: config.camiones.find((c) => ubicacionCamion(c.id) === valor)?.nombre ?? etiquetaLugar(valor),
  }));
  const origenes = [...lugares, ...camiones];

  const [origen, setOrigen] = useState('bodega');
  const [destino, setDestino] = useState('tienda');
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [guardando, setGuardando] = useState(false);

  const disponible = (productoId: string) =>
    existencias.find((e) => e.id === existenciaId(productoId, origen));

  /**
   * What a line moves is worth, at its cost where it leaves. Null for a quantity worth more than
   * the books take, as while a code scanned into it is still there (`enRango`): no figure and no
   * transfer, where the screen used to go down.
   */
  const valorDe = (l: Linea): Centavos | null => {
    const e = disponible(l.producto.id);
    const q = parseQuantity(l.cantidad);
    return e && q ? enRango(() => costOf(e.costoPromedio, q)) : ZERO;
  };
  const valorTotal = lineas.reduce((a, l) => a + (valorDe(l) ?? 0), 0) as Centavos;

  const valido =
    origen !== destino &&
    lineas.length > 0 &&
    lineas.every((l) => (parseQuantity(l.cantidad) ?? 0) > 0 && valorDe(l) !== null);

  return (
    <Pagina titulo={t('traslado.titulo')} volverA="/inventario">
      <section className="tarjeta mb-4 grid grid-cols-2 gap-4">
        <Selector
          etiqueta={t('traslado.saleDe')}
          value={origen}
          onChange={(e) => setOrigen(e.target.value)}
        >
          {origenes.map((u) => (
            <option key={u.valor} value={u.valor}>
              {u.nombre}
            </option>
          ))}
        </Selector>
        <Selector
          etiqueta={t('traslado.entraA')}
          value={destino}
          onChange={(e) => setDestino(e.target.value)}
        >
          {lugares.map((u) => (
            <option key={u.valor} value={u.valor}>
              {u.nombre}
            </option>
          ))}
        </Selector>
      </section>

      {origen === destino && (
        <p className="mb-4 text-sm text-amber-300">{t('traslado.distintos')}</p>
      )}

      <section className="tarjeta mb-4 flex flex-col gap-4">
        <BuscadorProducto
          onElegir={(p) =>
            setLineas((l) =>
              l.some((x) => x.producto.id === p.id) ? l : [...l, { key: newId(), producto: p, cantidad: '' }],
            )
          }
          etiqueta={t('pedidos.agregarProducto')}
          autoFocus
          lectorUsb
          preguntarEnHoja
        />

        {lineas.map((l) => {
          const e = disponible(l.producto.id);
          const q = parseQuantity(l.cantidad);
          const excede = e && q !== null && q > e.cantidad;
          return (
            <div key={l.key} className="flex items-end gap-3">
              <div className="min-w-0 flex-1">
                <Campo
                  etiqueta={nombre(l.producto)}
                  inputMode="decimal"
                  className="tabular"
                  value={l.cantidad}
                  onChange={(ev) =>
                    setLineas((xs) =>
                      xs.map((x) => (x.key === l.key ? { ...x, cantidad: ev.target.value } : x)),
                    )
                  }
                  ayuda={
                    e
                      ? t('traslado.hay', {
                          cantidad: formatQuantity(e.cantidad),
                          unidad: etiquetaUnidad(l.producto.unidadBase, l.producto.unidadBase),
                        }) + (excede ? t('traslado.excede') : '')
                      : t('traslado.sinExistencia')
                  }
                />
              </div>
              <button
                type="button"
                className="btn-texto mb-6 gap-1 text-xs text-slate-500"
                onClick={() => setLineas((xs) => xs.filter((x) => x.key !== l.key))}
              >
                <Ico icono="quitar" className="size-3.5" />
                {t('comun.quitar')}
              </button>
            </div>
          );
        })}
      </section>

      {verCostos && valorTotal > 0 && (
        <p className="mb-4 text-sm text-slate-400">
          {t('traslado.seMueven', { monto: formatMoney(valorTotal) })}
        </p>
      )}

      <button
        type="button"
        className="btn-primario w-full"
        disabled={!valido || guardando}
        onClick={async () => {
          setGuardando(true);
          await emitir('inventario.traslado', {
            trasladoId: newId(),
            origen,
            destino,
            fecha: hoy(),
            lineas: lineas.map((l) => ({
              productoId: l.producto.id,
              cantidadBase: parseQuantity(l.cantidad)!,
            })),
          });
          anunciar(t('traslado.registrado'));
          void volver({ to: '/inventario' });
        }}
      >
        <Ico icono="reparto" />
        {t(guardando ? 'compra.registrando' : 'traslado.registrar')}
      </button>
    </Pagina>
  );
}
